// DS9.13 Post Compact Card
import React from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import { PapiDeviation } from '@wix/da-papi-types';
import CommentOutlineIcon from '@wix/da-ds/pkg/Icons/24x24/CommentOutline';
import { Section } from '@wix/da-a11y/pkg/components/headings/tagWrappers';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks/useFlag';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import BiLink from '@wix/da-bi/pkg/Components/BiLink';
import type { BiEvent } from '@wix/da-bi/pkg/events';

import { useDeviationCoverImage } from '../DeviationViews/hooks/useDeviationCoverImage';
import UserAvatar from '../User/Avatar';
import UserLink from '../User/Link/UserLink';
import { DeviationTypes } from '../types/deviation';
import { getDeviationType, getDeviationUrl } from '../DeviationViews/_helpers';
import Dumbo from '../DeviationViews/Dumbo';
import Timestamp from '../Timestamp';
import ExcerptRenderer from '../TextRenderer/ExcerptRenderer';
import FaveControl from './FaveControl';

import { sanitizeHref } from '@wix/da-url';

import s from './PostCompactCard.scss';

const COVER_DIMENSIONS = {
  width: 300,
  height: 128,
};

export interface Props {
  deviation: PapiDeviation;
  showUsername?: boolean;
  showBackground?: boolean;
  className?: string;
  commentBiData?: BiEvent;
}

const PostCompactCard: React.FC<Props> = ({
  deviation,
  showUsername = false,
  showBackground = true,
  className,
  commentBiData,
}) => {
  const { t } = useTranslation();

  const deviationType = getDeviationType(deviation);
  const isJournal = deviationType === DeviationTypes.JOURNAL;
  const isStatusUpdate = deviationType === DeviationTypes.STATUS;

  const comments = deviation.stats?.comments || 0;

  const cover_attachment = useFlag('cover_attachment');
  const { coverImage } = useDeviationCoverImage({
    deviation,
    ...COVER_DIMENSIONS,
  });
  const hasBackgroundImage = cover_attachment
    ? showBackground && !!coverImage
    : showBackground && !!deviation.coverImage;

  return (
    <Section
      className={cn(
        s['root'],
        isJournal && s['journal'],
        isStatusUpdate && s['status-update'],
        hasBackgroundImage && s['with-background'],
        className
      )}
    >
      {hasBackgroundImage && (
        <div className={s['background']}>
          <Dumbo
            className={s['background-image']}
            deviation={cover_attachment ? deviation : deviation.coverImage || deviation}
            {...COVER_DIMENSIONS}
            useTextlessMode
          />
        </div>
      )}

      <div className={s['content']}>
        <div className={s['main-content']}>
          {!isStatusUpdate && <Heading className={s['title']}>{deviation.title}</Heading>}

          <div className={s['excerpt']}>
            {deviation.textContent && (
              <ExcerptRenderer textContent={deviation.textContent} htmlExcerptLegacy />
            )}
          </div>

          <div className={s['meta']}>
            <Timestamp timestamp={deviation.publishedTime} />
            <div className={s['divider']} />
            <span className={s['type']}>
              {isJournal
                ? t('postCompactCard.type.journal')
                : t('postCompactCard.type.statusUpdate')}
            </span>
          </div>
        </div>

        <div className={s['footer']}>
          {showUsername && deviation.author && (
            <div className={s['user-info']}>
              <UserAvatar
                className={s['avatar']}
                user={deviation.author}
                withTooltip={false}
                withLink={true}
                size={24}
              />
              <UserLink className={s['username']} user={deviation.author} showSymbol={false} />
            </div>
          )}

          <div className={s['stats']}>
            <FaveControl
              className={s['stat-button']}
              displayClassName={s['stat']}
              deviation={deviation}
              aria-label={t('postCompactCard.a11y.favourites')}
            />

            {commentBiData ? (
              <BiLink
                className={cn(s['stat-button'], s['stat'])}
                href={sanitizeHref(`${getDeviationUrl(deviation)}#comments`)}
                aria-label={t('postCompactCard.a11y.comments')}
                biData={commentBiData}
                stopPropagation
              >
                <CommentOutlineIcon className={s['stat-icon']} />
                <span className={s['stat-count']}>{comments}</span>
              </BiLink>
            ) : (
              <PlainButtonOrLink
                className={cn(s['stat-button'], s['stat'])}
                href={sanitizeHref(`${getDeviationUrl(deviation)}#comments`)}
                aria-label={t('postCompactCard.a11y.comments')}
              >
                <CommentOutlineIcon className={s['stat-icon']} />
                <span className={s['stat-count']}>{comments}</span>
              </PlainButtonOrLink>
            )}
          </div>
        </div>
      </div>
    </Section>
  );
};

PostCompactCard.displayName = 'PostCompactCard';

export default PostCompactCard;
